import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import {
  getCallCenterDocuments,
  getServicesDetail as getServicesDetailFetch,
} from "../../../core/services/managements/managements.service";
import {
  ManagementDetail,
  NewDocumentData,
  RequestStateDescriptionData,
} from "../../../core/models/managements/managements.model";
import {
  formatManagementDetail,
  getManagementsRequestTypeData,
  initialManagementDetail,
} from "../../../utils/components/managements/managements.util";
import { Box, Stack } from "@mui/material";
import theme from "../../../muiTheme";
import "./ServicesDetail.scss";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_OPEN,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
} from "../../../core/constants/constants";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { initialOwnerDataCup } from "../../../utils/ownerData/ownerData.util";
import ManagementsRequestState from "../../Managements/ManagementsRequestState/ManagementsRequestState";
import ServicesUpdateHeader from "../../ServicesUpdate/ServicesUpdateHeader/ServicesUpdateHeader";
import { getServiceTitleText } from "../../../utils/components/services/services.util";

const ServicesDetail = () => {
  const { idIssue } = useParams();
  const {
    userData,
    ownerData,
    isAgent,
    managementsDetailHeader: { recordTextType, claimTextSubType },
  } = useContext(TotalEnergiesContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [titleHeader, setTitleHeader] = useState<string>("");
  const [managementDetail, setManagementDetail] = useState<ManagementDetail>(
    initialManagementDetail
  );
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [refferenceNumber, setRefferenceNumber] = useState<number>(0);
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
    });
  const [filesAttached, setFilesAttached] = useState<NewDocumentData[]>([]);
  const [ownerDataCup, setOwnerDataCup] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  //ADD DIRECCION DE CORRESPONDENCIA
  const showManagementRequestInfo = ![
    MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  ].includes(claimTextSubType);
  const isUpdatePowersManagement =
    MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT === claimTextSubType;

  const getManagementDetail = useCallback(
    async (
      idIssue: number,
      token: string,
      claimTextSubType: string,
      isUpdatePowersManagement: boolean,
      abortSignal: AbortSignal
    ) => {
      await getServicesDetailFetch(idIssue, token, abortSignal)
        .then((managementDetail) => {
          const formattedManagementDetail = formatManagementDetail(
            managementDetail[0]
          );

          if (
            formattedManagementDetail.documents.some(
              (document) => document.FileName != null
            )
          ) {
            setHasDocuments(true);
            setRefferenceNumber(
              formattedManagementDetail.refferenceNumber
                ? formattedManagementDetail.refferenceNumber
                : 0
            );
          } else {
            setHasDocuments(false);
          }

          setManagementDetail(formattedManagementDetail);
          setRequestStateData(
            getManagementsRequestTypeData(
              claimTextSubType,
              formattedManagementDetail.state,
              isAgent ?? false
            )
          );
          setIsLoading(false);
          setStep((prevStep) =>
            [MANAGEMENTS_STATE_PENDING, MANAGEMENTS_STATE_OPEN].includes(
              formattedManagementDetail.state
            )
              ? prevStep
              : 3
          );
          isUpdatePowersManagement &&
            setOwnerDataCup(
              ownerData?.cups.find(
                ({ codeCups }) =>
                  codeCups === formattedManagementDetail.cupsCode
              ) || initialOwnerDataCup
            );
        })
        .catch(() => {
          setManagementDetail((prevManagementDetail) => ({
            ...prevManagementDetail,
            state: MANAGEMENTS_STATE_ERROR,
          }));
          setRequestStateData(
            getManagementsRequestTypeData(
              claimTextSubType,
              MANAGEMENTS_STATE_ERROR,
              isAgent ?? false
            )
          );
          setIsLoading(false);
        });
    },
    [ownerData?.cups]
  );

  useEffect(() => {
    const managementDetailAbortSignal = new AbortController();
    if (userData && idIssue) {
      setIsLoading(true);
      setStep(1);
      setTitleHeader(getServiceTitleText(recordTextType, claimTextSubType));
      getManagementDetail(
        +idIssue,
        userData.token,
        claimTextSubType,
        isUpdatePowersManagement,
        managementDetailAbortSignal.signal
      );

      return () => managementDetailAbortSignal.abort();
    }
  }, [
    claimTextSubType,
    isUpdatePowersManagement,
    getManagementDetail,
    idIssue,
    recordTextType,
    userData,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasDocuments && refferenceNumber !== 0) {
        setIsLoadingFiles(true);
        if (userData) {
          try {
            const response = await getCallCenterDocuments(
              refferenceNumber,
              userData.token
            );
            const newDocuments = response.filter(
              (doc) =>
                !filesAttached.some(
                  (existingDoc) => existingDoc.IdDocumento === doc.IdDocumento
                )
            );
            setFilesAttached((prevFiles) => [...prevFiles, ...newDocuments]);
          } catch (error) {
            console.error("Error downloading document:", error);
          }
          setIsLoadingFiles(false);
        }
      }
    };

    fetchData();
  }, [filesAttached, hasDocuments, refferenceNumber, userData]);

  return (
    <Box component="section" className="managements-detail">
      <ServicesUpdateHeader
        title={titleHeader}
        isLoading={isUpdatePowersManagement && isLoading}
        step={step}
        showDetailCups={isUpdatePowersManagement}
        cupsDetail={{
          cupsCode: ownerDataCup.codeCups,
          rate: ownerDataCup.fee,
          address: ownerDataCup.address,
          service: ownerDataCup.envirorment,
        }}
      />
      <Stack
        component="article"
        direction="column"
        justifyContent="center"
        alignItems="center"
        paddingTop={"2.5rem"}
        bgcolor={{ md: theme.palette.grey[50] }}
      >
        <ManagementsRequestState
          showManagementRequestInfo={showManagementRequestInfo}
          isLoading={isLoading}
          state={managementDetail.state}
          managementRequestInfo={managementDetail}
          title={requestStateData.title}
          description={requestStateData.description}
          descriptionExtra={requestStateData.descriptionExtra}
          loadingFiles={isLoadingFiles}
          files={filesAttached}
        />
      </Stack>
    </Box>
  );
};

export default ServicesDetail;
